import { db } from "../db";
import type { StorageMode } from "../model";
import { apiRequest, authHeaders } from "./cloudLibrary";

export interface AudioStorage {
  readonly mode: StorageMode;
  put(trackId: string, file: File): Promise<string>;
  getBlob(trackId: string): Promise<Blob>;
  getPlaybackUrl(trackId: string): Promise<string>;
  remove(trackId: string): Promise<void>;
  download(trackId: string): Promise<Blob>;
}

export class LocalAudioStorage implements AudioStorage {
  readonly mode = "local" as const;
  async put(trackId: string, file: File) {
    await db.audio.put({ trackId, blob: file });
    return trackId;
  }
  async getPlaybackUrl(trackId: string) {
    return URL.createObjectURL(await this.getBlob(trackId));
  }
  async getBlob(trackId: string) { const record = await db.audio.get(trackId); if (!record) throw new Error("The original local MP3 is unavailable in this browser."); return record.blob; }
  async remove(trackId: string) { await db.audio.delete(trackId); }
  async download(trackId: string) { return this.getBlob(trackId); }
}

export class R2AudioStorage implements AudioStorage {
  readonly mode = "r2" as const;
  constructor(private readonly apiBaseUrl: string) {}
  async put(trackId: string, file: File) {
    const response = await fetch(`${this.apiBaseUrl}/api/audio/${trackId}`, {
      method: "PUT",
      headers: authHeaders({ "Content-Type": file.type || "audio/mpeg" }),
      body: file,
    });
    if (!response.ok) throw new Error(`R2 upload failed (${response.status}).`);
    return `audio/${trackId}.mp3`;
  }
  async getPlaybackUrl(trackId: string) {
    return URL.createObjectURL(await this.getBlob(trackId));
  }
  async getBlob(trackId: string) {
    const response = await apiRequest(this.apiBaseUrl, `/api/audio/${trackId}`);
    if (!response.ok) throw new Error(`R2 playback failed (${response.status}).`);
    return response.blob();
  }
  async remove(trackId: string) {
    await apiRequest(this.apiBaseUrl, `/api/tracks/${trackId}`, { method: "DELETE" });
  }
  async download(trackId: string) { return (await apiRequest(this.apiBaseUrl, `/api/audio/${trackId}?download=1`)).blob(); }
}

export function createStorage(mode: StorageMode, apiBaseUrl: string): AudioStorage {
  if (mode === "local") return new LocalAudioStorage();
  return new R2AudioStorage(apiBaseUrl);
}
